import { memo, useState, Suspense } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { PropertyHeader } from "./components/PropertyHeader";
import { PropertyInfo } from "./components/PropertyInfo";
import { PropertyTabs } from "./components/PropertyTabs";
import { PropertyActions } from "./components/PropertyActions";
import type { Property } from "@/types/property";

interface PropertyCardProps {
  property: Property;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const LoadingCard = () => (
  <Card className="overflow-hidden flex flex-col h-full p-6 items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin" />
  </Card>
);

export const PropertyCard = memo(({ property, onEdit, onDelete }: PropertyCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(property.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Suspense fallback={<LoadingCard />}>
      <Card className="overflow-hidden flex flex-col h-full">
        <PropertyHeader property={property} />
        
        <div className="p-6 space-y-6 flex-grow flex flex-col">
          <PropertyInfo property={property} />
          <PropertyTabs property={property} />
          <PropertyActions
            onEdit={() => onEdit(property.id)}
            onDelete={handleDelete}
            isDeleting={isDeleting}
          />
        </div>
      </Card>
    </Suspense>
  );
});

PropertyCard.displayName = 'PropertyCard';